import { useCallback, useEffect, useState } from 'react'
import {
  getLocalStorage,
  localStorageParentKeys,
  setLocalStorage,
} from '@opallabs/principles'
import {
  GEM_COPILOT_SIDEBAR_WIDTH,
  GEM_PANEL_MAX_WIDTH_RATIO,
  GEM_PANEL_MIN_WIDTH,
  MONOLITH_MIN_WIDTH,
  gemPanelInlineSizeVariable,
  gemPanelTransitionVariable,
} from './shared'

const STORAGE_KEY = 'panel-width'

/**
 * Half the viewport, or whatever the monolith frame can spare, whichever is
 * less, but never less than the width the panel opens at. Under a 1650px
 * window the frame cannot spare the default, and a laptop still has to be able
 * to open the panel at it; the frame is overlapped instead — the laptop case
 * the panel cannot solve.
 */
export const maxPanelWidth = (): number =>
  Math.max(
    Math.min(
      Math.round(window.innerWidth * GEM_PANEL_MAX_WIDTH_RATIO),
      window.innerWidth - MONOLITH_MIN_WIDTH
    ),
    GEM_COPILOT_SIDEBAR_WIDTH
  )

export const clampPanelWidth = (
  width: number,
  max: number = maxPanelWidth()
): number => Math.min(Math.max(Math.round(width), GEM_PANEL_MIN_WIDTH), max)

// The width the user asked for, which is not always the width they got: it is
// clamped as it is rendered, so a width chosen on a wide screen is still there
// when they come back to one rather than being ground down by every narrow
// window they visited in between.
const storedPanelWidth = (): number | null => {
  const stored = getLocalStorage<unknown>({
    key: STORAGE_KEY,
    namespace: localStorageParentKeys.GEM_COPILOT,
  })
  return typeof stored === 'number' && Number.isFinite(stored) ? stored : null
}

const savePanelWidth = (width: number) =>
  setLocalStorage({
    key: STORAGE_KEY,
    namespace: localStorageParentKeys.GEM_COPILOT,
    value: width,
  })

const writePanelWidth = (width: number) =>
  document.body.style.setProperty(
    gemPanelInlineSizeVariable,
    `${clampPanelWidth(width)}px`
  )

type PanelWidth = {
  panelWidth: number
  /** Commits a width: it re-renders the panel and is saved for next time. */
  setPanelWidth: (width: number) => void
  /** Shows a width without re-rendering, for the frames of a drag. */
  previewPanelWidth: (width: number) => void
}

/**
 * The Gem panel's width, published as a CSS variable on `<body>` so the views
 * that make room for the panel can follow a drag without re-rendering.
 */
export const usePanelWidth = (): PanelWidth => {
  const [chosenWidth, setChosenWidth] = useState<number>(
    () => storedPanelWidth() ?? GEM_COPILOT_SIDEBAR_WIDTH
  )
  // A narrower window leaves the frame less room, so the limit belongs in the
  // render: the same choice yields a different width as the window changes.
  const [maxWidth, setMaxWidth] = useState<number>(maxPanelWidth)
  const panelWidth = clampPanelWidth(chosenWidth, maxWidth)

  const previewPanelWidth = useCallback((width: number) => {
    document.body.style.setProperty(gemPanelTransitionVariable, '0s')
    writePanelWidth(width)
  }, [])

  // Saved here and nowhere else. An effect keyed on the width would also run on
  // mount, and the provider mounts on every authenticated page, so one visit on
  // a laptop would write back the width that window allows and lose the one the
  // user picked.
  const setPanelWidth = useCallback((width: number) => {
    document.body.style.removeProperty(gemPanelTransitionVariable)
    const chosen = clampPanelWidth(width)
    setChosenWidth(chosen)
    savePanelWidth(chosen)
  }, [])

  useEffect(() => {
    writePanelWidth(panelWidth)
  }, [panelWidth])

  useEffect(() => {
    const onResize = () => setMaxWidth(maxPanelWidth())
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  useEffect(
    () => () => {
      document.body.style.removeProperty(gemPanelInlineSizeVariable)
      document.body.style.removeProperty(gemPanelTransitionVariable)
    },
    []
  )

  return { panelWidth, setPanelWidth, previewPanelWidth }
}
